import React from "react";
import { Loading } from "spark-core-dx/components";
import { normalizeDataTypeLabel } from "./utils";

const TinyIcon = ({ children, title }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden={title ? undefined : true} role={title ? "img" : undefined}>
    {title ? <title>{title}</title> : null}
    {children}
  </svg>
);

const SparkIcon = () => (
  <TinyIcon title="AI suggest mapping">
    <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M18.5 15.5l.8 2 .2.2 2 .8-2 .8-.2.2-.8 2-.8-2-.2-.2-2-.8 2-.8.2-.2.8-2z" fill="currentColor" />
  </TinyIcon>
);

const EditIcon = () => (
  <TinyIcon title="Edit entity field">
    <path d="M4 20h4l10-10a2.1 2.1 0 0 0-4-4L4 16v4z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </TinyIcon>
);

const ReorderIcon = () => (
  <TinyIcon title="Reorder entity field">
    <path d="M12 4l-3 3h2v10h2V7h2l-3-3zm0 16l3-3h-2V7h-2v10H9l3 3z" fill="currentColor" />
  </TinyIcon>
);

const DragIndicatorIcon = () => (
  <svg viewBox="0 0 24 24" width="40" height="40" aria-hidden="true" role="img">
    <title>Drag</title>
    <path
      d="M9 7a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm6-8a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
      fill="currentColor"
    />
  </svg>
);

const IconButton = ({ title, onClick, children }) => (
  <button type="button" className="efmd-row__icon-btn" title={title} aria-label={title} onClick={onClick}>
    {children}
  </button>
);

const formatTypeLabel = (field) => normalizeDataTypeLabel(field?.dataTypeName || field?.dataTypeId || "Unknown");

const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const FieldRow = ({
  field,
  side,
  isMapped,
  onStartConnect,
  onQuickFilter,
  onEditEntityField,
  onAiSuggestField,
  setRef,
  onStartReorder,
  onDragOverRow,
  onDropOnRow,
  onEndReorder,
  isReordering,
  onPressReorder,
  onReleaseReorder,
  disableReorder,
  aiSuggestLoading,
}) => {
  const titleText = field.displayName || field.fieldName;
  const typeText = formatTypeLabel(field);
  const descriptionText = stripHtml(field?.description);
  const descriptionTitle = descriptionText || "No description";

  return (
    <div
      className={`efmd-row efmd-row--${side} ${isMapped ? "is-mapped" : ""} ${isReordering ? "is-reordering" : ""}`}
      ref={setRef}
      onDragOver={
        side === "entity"
          ? (event) => {
              onDragOverRow?.(field, event);
            }
          : undefined
      }
      onDrop={
        side === "entity"
          ? (event) => {
              onDropOnRow?.(field, event);
            }
          : undefined
      }
    >
      {side === "entity" && (
        <button
          type="button"
          className="efmd-row__drag-handle"
          onMouseDown={(event) => {
            event.stopPropagation();
            onStartConnect(field, event);
          }}
          title={`Drag to map ${field.displayName || field.fieldName}`}
          aria-label={`Drag to map ${field.displayName || field.fieldName}`}
          onDoubleClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onQuickFilter(field);
          }}
        >
          <DragIndicatorIcon />
        </button>
      )}
      {side === "entity" && (
        <div className="efmd-row__actions">
          <IconButton title={"AI suggest mapping"} onClick={() => onAiSuggestField(field)}>
            {aiSuggestLoading ? (
              <span className="efmd-ai-loading">
                <Loading />
              </span>
            ) : (
              <SparkIcon />
            )}
          </IconButton>
        </div>
      )}
      <button type="button" className="efmd-row__main" title={`Field Name : ${field.fieldName || ""}`}>
        <div className="efmd-row__title">
          <span className="efmd-row__title-name">{titleText}</span>
          <span className="efmd-row__title-type">({typeText})</span>
        </div>
        <div className="efmd-row__meta">
          <span className="efmd-row__meta-description" title={descriptionTitle}>
            {descriptionTitle}
          </span>
        </div>
      </button>
      <div className="efmd-row__actions">
        {side === "source" ? (
          <>
            <IconButton title={"AI suggest mapping"} onClick={() => onAiSuggestField(field)}>
              {aiSuggestLoading ? (
                <span className="efmd-ai-loading">
                  <Loading />
                </span>
              ) : (
                <SparkIcon />
              )}
            </IconButton>
            <button
              type="button"
              className="efmd-row__drag-handle"
              onMouseDown={(event) => {
                event.stopPropagation();
                onStartConnect(field, event);
              }}
              title={`Drag to map ${field.displayName || field.fieldName}`}
              aria-label={`Drag to map ${field.displayName || field.fieldName}`}
              onDoubleClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onQuickFilter(field);
              }}
            >
              <DragIndicatorIcon />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={`efmd-row__icon-btn efmd-row__icon-btn--reorder ${disableReorder ? "is-disabled" : ""}`}
              title={`Drag to reorder ${field.displayName || field.fieldName}`}
              aria-label={`Drag to reorder ${field.displayName || field.fieldName}`}
              draggable={!disableReorder}
              disabled={disableReorder}
              onMouseDown={() => !disableReorder && onPressReorder?.(field)}
              onMouseUp={() => onReleaseReorder?.()}
              onDragStart={(event) => {
                if (disableReorder) {
                  event.preventDefault();
                  return;
                }
                const rowElement = event.currentTarget.closest(".efmd-row");
                onStartReorder?.(field, event, rowElement);
              }}
              onDragEnd={() => onEndReorder?.()}
            >
              <ReorderIcon />
            </button>
            <IconButton title={"Edit entity field"} onClick={() => onEditEntityField(field)}>
              <EditIcon />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
};

export default FieldRow;
